import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { ARCHIVE_DEFAULT_HOLD_DAYS } from '@zerogap/shared';
import { get, post, qs } from '../api';
import { arrow, locale, msg, tr } from '../i18n';
import { useSession, useTenant } from '../session';
import { ArchiveBrowser, folderName, useArchives } from './archives';
import { DayPicker, KeptFilesTree, shortDate, type Choices } from './restore-files';
import { ShareBar, ShareDialog } from './shares';
import {
  ago, Badge, bytes, Button, Card, ConfirmDialog, Empty, ErrorBox, Field, friendlyError, Icons, Loading, OptionCards, PageHead, Progress, RowLink, Segmented, TechDetails,
  useStepUp, useToast, vdisplay, when,
} from '../ui';

interface Entry {
  id: number;
  path: string;
  name: string;
  kind: string;
  size: number;
  mtime: string | null;
  deletedLater: boolean;
  link_target: string | null;
  changed: boolean;
  /** How many versions of this file are kept across all backups. */
  versions: number;
}

/** Browse one saved backup; optional multi-selection of files/folders (folders restore everything inside). */
export function RecoveryPointBrowser({ rpId, deviceId, pathStyle, selectable, selected, onToggle, rootLabel }: {
  rpId: string;
  deviceId: string;
  pathStyle: 'windows' | 'posix';
  /** What the top level is called; a phone's files start at "My Files". */
  rootLabel?: string;
  selectable?: boolean;
  selected?: Set<string>;
  onToggle?: (path: string) => void;
}) {
  const t = useTenant();
  const nav = useNavigate();
  const [path, setPath] = useState('/');
  const [search, setSearch] = useState('');
  const [versionsOf, setVersionsOf] = useState<string | null>(null);
  // Sharing (docs/sharing.md): outside the restore wizard, "Share" turns on checkboxes for choosing what to share.
  const canShare = !selectable && t.can('shares.create');
  const [sharing, setSharing] = useState(false);
  const [shareSel, setShareSel] = useState<Set<string>>(new Set());
  const [sharePaths, setSharePaths] = useState<string[] | null>(null);
  const checks = selectable || sharing;
  const chosen = sharing ? shareSel : selected;
  const toggle = (p: string) => {
    if (!sharing) return onToggle?.(p);
    const next = new Set(shareSel);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setShareSel(next);
  };
  const page = useInfiniteQuery({
    queryKey: ['browse', rpId, path],
    queryFn: ({ pageParam }) => get(`/api/t/${t.tenantId}/recovery-points/${rpId}/browse${qs({ path, after: pageParam ?? undefined, limit: 200 })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last: { entries: Entry[]; next: string | null }) => last.next,
  });
  const found = useQuery({
    queryKey: ['search', rpId, search],
    queryFn: () => get(`/api/t/${t.tenantId}/recovery-points/${rpId}/search${qs({ q: search })}`),
    enabled: search.trim().length >= 2,
  });
  const entries: Entry[] = page.data?.pages.flatMap((p) => p.entries) ?? [];
  // Skip through single-folder levels (e.g. "/" -> "C:" -> "Users" -> ...) on open. The level it stops
  // at is the top of what was backed up: "This PC" leads there, and the skipped levels are not links,
  // since each would only show the one folder below it.
  const [top, setTop] = useState<string | null>(null);
  useEffect(() => {
    if (top !== null || !page.data || page.isFetching) return;
    const first = page.data.pages[0]!.entries;
    if (page.data.pages.length === 1 && first.length === 1 && first[0]!.kind === 'd') setPath(first[0]!.path);
    else setTop(path);
  }, [page.data, page.isFetching]); // eslint-disable-line react-hooks/exhaustive-deps
  const home = top ?? '/';
  const crumbs = path === '/' ? [] : path.slice(1).split('/');
  const crumbPath = (i: number) => '/' + crumbs.slice(0, i + 1).join('/');
  const above = (p: string) => home.startsWith(p + '/');
  const isSel = (p: string) => !!chosen && [...chosen].some((s) => p === s || p.startsWith(s + '/') || s === '/');
  const rows: Entry[] = search.trim().length >= 2 ? (found.data?.results ?? []) : entries;
  // Outside the restore wizard a file has one plain action: get it back. The version picker opens only
  // when there is more than one version to choose from; with one, the wizard takes its newest kept copy.
  const restore = (e: Entry) =>
    e.versions > 1 ? setVersionsOf(e.path) : nav(`${t.base}/restore?device=${deviceId}&path=${encodeURIComponent(e.path)}`);
  const action = (e: Entry) => {
    if (e.kind === 'd') return null;
    if (sharing) return null;
    if (!selectable && t.can('restores.create')) return <Button size="sm" variant="ghost" onClick={() => restore(e)}>{tr('Restore this file')}</Button>;
    if (e.versions > 1) return <Button size="sm" variant="ghost" onClick={() => setVersionsOf(e.path)}>{tr('Earlier versions')}</Button>;
    return null;
  };
  return (
    <div className="card">
      <div className="card-head">
        <div className="crumbs" aria-label={tr('Location')}>
          <button onClick={() => { setPath(home); setSearch(''); }}>{rootLabel ?? (pathStyle === 'windows' ? tr('This PC') : '/')}</button>
          {crumbs.map((c, i) => (
            <span key={i} className="row" style={{ gap: 4 }}>
              <span className="muted">{arrow.crumb()}</span>
              {above(crumbPath(i))
                ? <span className="muted"><bdi>{c}</bdi></span>
                : <button onClick={() => { setPath(crumbPath(i)); setSearch(''); }}><bdi>{c}</bdi></button>}
            </span>
          ))}
        </div>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input type="search" placeholder={tr('Search by file name…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 240 }} aria-label={tr('Search this backup')} />
          {canShare && !sharing && <Button size="sm" onClick={() => setSharing(true)}><Icons.share />{tr('Share')}</Button>}
        </div>
      </div>
      {page.error ? <div className="card-pad"><ErrorBox error={page.error} /></div> : (
        <div className="table-wrap" style={{ maxHeight: 520, overflow: 'auto' }}>
          <table>
            <thead><tr>{checks && <th style={{ width: 36 }} />}<th>{tr('Name')}</th><th className="right">{tr('Size')}</th><th>{tr('Last changed')}</th><th /></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="fb-row">
                  {checks && (
                    <td>
                      <input type="checkbox" aria-label={tr('Select {name}', { name: e.name })} checked={isSel(e.path)} disabled={isSel(e.path) && !chosen!.has(e.path)} onChange={() => toggle(e.path)} />
                    </td>
                  )}
                  <td>
                    <div className="fname">
                      {e.kind === 'd' ? <Icons.folder className="ficon dir" /> : e.kind === 'l' || e.kind === 'j' ? <Icons.link className="ficon" /> : <Icons.file className="ficon" />}
                      {e.kind === 'd' ? (
                        <button className="btn ghost sm" style={{ padding: '2px 4px' }} onClick={() => { setPath(e.path); setSearch(''); }}><bdi>{e.name}</bdi></button>
                      ) : (
                        <bdi>{search ? vdisplay(e.path, pathStyle) : e.name}</bdi>
                      )}
                      {e.deletedLater && <Badge tone="neutral" dot={false}>{tr('changed or deleted later')}</Badge>}
                      {e.changed && <Badge tone="warn">{tr('was being edited during the backup')}</Badge>}
                    </div>
                  </td>
                  <td className="right num small">{e.kind === 'd' ? '' : bytes(e.size)}</td>
                  <td className="small">{e.mtime ? when(e.mtime) : ''}</td>
                  <td className="right">{action(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(page.isLoading || found.isLoading) && <div className="card-pad"><Loading rows={3} /></div>}
          {!page.isLoading && rows.length === 0 && <Empty glyph="∅" title={search ? tr('No matches') : tr('This folder is empty in this backup')} />}
          {!search && page.hasNextPage && <div className="card-pad"><Button onClick={() => page.fetchNextPage()} busy={page.isFetchingNextPage}>{tr('Show more')}</Button></div>}
        </div>
      )}
      {sharing && (
        <div className="card-pad">
          <ShareBar
            count={shareSel.size}
            onShare={() => setSharePaths([...shareSel])}
            onCancel={() => { setSharing(false); setShareSel(new Set()); }}
            extra={<Button variant="ghost" onClick={() => setSharePaths([path])}>{tr('Share everything in this folder')}</Button>}
          />
        </div>
      )}
      {sharePaths && (
        <ShareDialog
          source={{ recoveryPointId: rpId }}
          paths={sharePaths}
          onClose={() => { setSharePaths(null); setSharing(false); setShareSel(new Set()); }}
        />
      )}
      {versionsOf && <Versions deviceId={deviceId} path={versionsOf} pathStyle={pathStyle} onClose={() => setVersionsOf(null)} />}
    </div>
  );
}

function Versions({ deviceId, path, pathStyle, onClose }: { deviceId: string; path: string; pathStyle: 'windows' | 'posix'; onClose: () => void }) {
  const t = useTenant();
  const nav = useNavigate();
  const v = useQuery({ queryKey: ['versions', deviceId, path], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/versions${qs({ path })}`) });
  return createPortal(
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide" role="dialog" aria-modal>
        <div className="modal-head"><h2>{tr('Earlier versions')}</h2><div className="mono small muted" dir="ltr">{vdisplay(path, pathStyle)}</div></div>
        <div className="modal-body">
          {v.isLoading ? <Loading /> : (
            <table>
              <thead><tr><th>{tr('Last changed')}</th><th className="right">{tr('Size')}</th><th>{tr('Kept in backups from')}</th><th /></tr></thead>
              <tbody>
                {v.data?.versions.map((x: any) => (
                  <tr key={x.id}>
                    <td className="small">{when(x.mtime)}</td>
                    <td className="right num small">{bytes(x.size)}</td>
                    <td className="small">{x.recovery_points.slice(0, 3).map((r: any) => when(r.finishedAt)).join(' · ')}{x.recovery_points.length > 3 ? ` +${x.recovery_points.length - 3}` : ''}</td>
                    <td className="right">{t.can('restores.create') && <Button size="sm" onClick={() => nav(`${t.base}/restore?device=${deviceId}&path=${encodeURIComponent(path)}&seq=${x.seq}`)}>{tr('Get this version back')}</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-foot"><Button onClick={onClose}>{tr('Close')}</Button></div>
      </div>
    </div>,
    document.body,
  );
}

type Dest = 'browser' | 'alternate' | 'original' | 'device';

const today = () => new Date().toISOString().slice(0, 10);
const restoreFolder = (os: string | undefined) => (os === 'windows' ? `C:\\Restored files\\${today()}` : `/srv/restored-files/${today()}`);
/** The API takes at most this many separate items in one restore. */
const MAX_ITEMS = 1000;

type StepKey = 'who' | 'source' | 'files' | 'where' | 'check';
type HoldChoice = '7' | '30' | '90' | 'forever';

export function RestoreWizard() {
  const t = useTenant();
  const { me } = useSession();
  const [sp] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const toast = useToast();
  const stepUp = useStepUp(!!me?.user.mfaEnabled);
  // Paths handed over by the archive page ("Get the chosen files back").
  const handed = (loc.state as { paths?: string[] } | null)?.paths;
  const [stepKey, setStepKey] = useState<StepKey>('who');
  const [deviceId, setDeviceId] = useState(sp.get('device') ?? '');
  const [source, setSource] = useState<'backup' | 'archive'>(sp.get('source') === 'archive' ? 'archive' : 'backup');
  const [archiveId, setArchiveId] = useState(sp.get('archive') ?? '');
  // Files first (docs/restore-files-first.md): what is ticked in the tree of everything kept, each with the
  // backup its copy comes from. "As it was on a day" instead browses one backup (dayRp) and ticks paths in
  // `selected`, as an archive does.
  const [choices, setChoices] = useState<Choices>(new Map());
  const [dayRp, setDayRp] = useState(sp.get('rp') ?? '');
  const [pickingDay, setPickingDay] = useState(false);
  const [switchTo, setSwitchTo] = useState<null | 'day' | 'newest'>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set(handed ?? (sp.get('rp') && sp.get('path') ? [sp.get('path')!] : [])));
  const [dest, setDest] = useState<Dest>(sp.get('source') === 'archive' ? 'original' : 'browser');
  const [hold, setHold] = useState<HoldChoice>(String(ARCHIVE_DEFAULT_HOLD_DAYS) as HoldChoice);
  const [destDevice, setDestDevice] = useState('');
  const [destPath, setDestPath] = useState('');
  const [conflict, setConflict] = useState<'rename' | 'skip' | 'overwrite'>('rename');
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [meta, setMeta] = useState(true);
  const [perms, setPerms] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const devices = useQuery({ queryKey: ['devices-all', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/devices?limit=200`) });
  const all: any[] = devices.data?.devices ?? [];
  const srcDevice = all.find((d) => d.id === deviceId);
  const isComputer = srcDevice?.kind !== 'phone';
  const archivesQ = useArchives(deviceId, !!srcDevice && isComputer);
  // Archives that hold something in the cloud; a computer without any skips the "from where" step.
  const archives = (archivesQ.data?.archives ?? []).filter((a) => a.cloud.files > 0);
  const archive = archives.find((a) => a.id === archiveId);
  const fromArchive = source === 'archive';
  const mode: 'archive' | 'day' | 'kept' = fromArchive ? 'archive' : dayRp ? 'day' : 'kept';
  const dayInfo = useQuery({ queryKey: ['rp', dayRp], queryFn: () => get(`/api/t/${t.tenantId}/recovery-points/${dayRp}`), enabled: !!dayRp });
  const dayTime: string | null = dayInfo.data?.recoveryPoint?.finished_at ?? null;
  const chosen = [...choices.values()];
  const older = chosen.filter((c) => c.olderFrom);
  const count = mode === 'kept' ? choices.size : selected.size;
  const estimate = useQuery({
    queryKey: ['estimate', mode, mode === 'archive' ? archiveId : mode === 'day' ? dayRp : deviceId, mode === 'kept' ? chosen.map((c) => `${c.path}@${c.seq}`).join('|') : [...selected].join('|')],
    queryFn: () => post(`/api/t/${t.tenantId}/restores/estimate`,
      mode === 'archive' ? { folderArchiveId: archiveId, paths: [...selected] }
        : mode === 'day' ? { recoveryPointId: dayRp, paths: [...selected] }
        : { deviceId, items: chosen.map((c) => ({ path: c.path, seq: c.seq })) }),
    enabled: (mode !== 'archive' || !!archiveId) && count > 0 && count <= MAX_ITEMS,
  });
  const keys: StepKey[] = [
    'who',
    ...(archives.length > 0 ? (['source'] as StepKey[]) : []),
    'files',
    'where',
    'check',
  ];
  const step = Math.max(0, keys.indexOf(stepKey));
  // Deep links: a file (and the backup of the copy wanted), a backup to browse as it was, or an archive
  // (and files) open at the right step, once the computer's archives are known (they decide whether there
  // is a "from where" step at all).
  const [linked, setLinked] = useState(false);
  useEffect(() => {
    if (linked || !sp.get('device') || devices.isLoading || (isComputer && archivesQ.isLoading)) return;
    setLinked(true);
    if (sp.get('source') === 'archive' && sp.get('archive')) setStepKey(selected.size ? 'where' : 'files');
    else if (sp.get('rp')) setStepKey(sp.get('path') ? 'where' : 'files');
    else if (sp.get('path')) void chooseLinked(sp.get('path')!, sp.get('seq'));
  }, [devices.isLoading, archivesQ.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  // A linked file comes back as the copy named by seq, or its newest kept copy.
  const chooseLinked = async (path: string, seq: string | null) => {
    try {
      const v = await get(`/api/t/${t.tenantId}/devices/${sp.get('device')}/versions${qs({ path })}`);
      const copies: any[] = v.versions ?? [];
      const i = Math.max(0, copies.findIndex((x) => String(x.seq) === seq));
      const x = copies[i];
      if (!x) return setStepKey('files');
      setChoices(new Map([[path, { path, seq: x.seq, kind: x.kind, olderFrom: i === 0 ? null : (x.mtime ?? x.recovery_points.at(-1)?.finishedAt ?? null) }]]));
      setStepKey('where');
    } catch {
      setStepKey('files');
    }
  };
  useEffect(() => { if (fromArchive && !archiveId && archives.length === 1) setArchiveId(archives[0]!.id); }, [fromArchive, archives.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const pathStyle: 'windows' | 'posix' = srcDevice?.os === 'windows' ? 'windows' : 'posix';
  const others = all.filter((d) => d.status === 'active' && d.id !== deviceId);
  const target = dest === 'device' ? others.find((d) => d.id === destDevice) : srcDevice;
  const pick = (d: Dest) => {
    setDest(d);
    if (d === 'alternate') setDestPath(restoreFolder(srcDevice?.os));
    if (d === 'device') setDestPath(restoreFolder(others.find((o) => o.id === destDevice)?.os ?? srcDevice?.os));
  };
  const chooseSource = (v: 'backup' | 'archive') => {
    setSource(v);
    setSelected(new Set());
    setChoices(new Map());
    setDayRp('');
    setPickingDay(false);
    // Files from an archive usually go back where they were; a backup's default stays the ZIP download.
    setDest(v === 'archive' ? 'original' : 'browser');
  };
  // Moving between the tree and "as it was on a day" starts the ticking again, after asking if anything is ticked.
  const toDay = () => (choices.size ? setSwitchTo('day') : setPickingDay(true));
  const toNewest = () => (selected.size ? setSwitchTo('newest') : setDayRp(''));
  const doSwitch = () => {
    if (switchTo === 'day') {
      setChoices(new Map());
      setPickingDay(true);
    } else {
      setSelected(new Set());
      setDayRp('');
    }
    setSwitchTo(null);
  };
  const titles: Record<StepKey, string> = {
    who: tr('Whose files'),
    source: tr('From where'),
    files: tr('Which files'),
    where: tr('Where to'),
    check: tr('Check and start'),
  };
  const toggle = (p: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(p)) n.delete(p);
    else n.add(p);
    for (const x of [...n]) if (x !== p && x.startsWith(p + '/')) n.delete(x);
    return n;
  });
  const destOk = dest === 'browser' || dest === 'original' || (!!destPath.trim() && (dest !== 'device' || !!destDevice));
  const canNext: Record<StepKey, boolean> = {
    who: !!deviceId,
    source: fromArchive ? !!archiveId : true,
    files: count > 0 && count <= MAX_ITEMS && (mode !== 'archive' || !!archive),
    where: destOk,
    check: true,
  };
  const holdText = () =>
    hold === 'forever' ? tr('They stay on the computer until you let them move again.') : tr('They stay on the computer for {n} days before they can move to the cloud again.', { n: Number(hold) });
  const copiesText = () =>
    older.length === 0 ? tr('the newest copies') :
    older.length === chosen.length ? (older.length === 1 ? tr('the copy from {date}', { date: shortDate(older[0]!.olderFrom!) }) : tr('copies from earlier backups')) :
    older.length === 1 ? tr('the newest copies, and 1 file from {date}', { date: shortDate(older[0]!.olderFrom!) }) :
    tr('the newest copies, and {n} files from earlier backups', { n: older.length });
  const summary = () => {
    const what = typeof estimate.data?.files === 'number' ? tr('{files} files ({size})', { files: Number(estimate.data.files).toLocaleString(locale()), size: bytes(estimate.data.bytes) }) : tr('the chosen files');
    const where =
      dest === 'browser' ? tr('download them as a ZIP file') :
      dest === 'original' ? tr('put them back where they were on {name}', { name: srcDevice?.display_name ?? '' }) :
      tr('save them to {path} on {name}', { path: `⁦${destPath}⁩`, name: target?.display_name ?? '' });
    if (mode === 'archive') {
      const text = tr('Get {what} from the archive {folder} of {name}, and {where}.', { what, folder: archive ? folderName(archive.path, pathStyle) : '', name: srcDevice?.display_name ?? '', where });
      return dest === 'original' ? `${text} ${holdText()}` : text;
    }
    if (mode === 'kept') return tr('Get {what} from {name} ({copies}), and {where}.', { what, name: srcDevice?.display_name ?? '', copies: copiesText(), where });
    return tr('Get {what} from {name} as they were on {when}, and {where}.', { what, name: srcDevice?.display_name ?? '', when: dayTime ? when(dayTime) : '', where });
  };
  const submit = () => stepUp.run(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await post(`/api/t/${t.tenantId}/restores`, {
        sourceDeviceId: deviceId,
        ...(mode === 'archive'
          ? {
              folderArchiveId: archiveId,
              paths: [...selected],
              ...(dest === 'original' ? (hold === 'forever' ? { archiveHoldUntilReleased: true } : { archiveHoldDays: Number(hold) }) : {}),
            }
          : mode === 'day' ? { recoveryPointId: dayRp, paths: [...selected] }
          : { items: chosen.map((c) => ({ path: c.path, seq: c.seq })) }),
        destinationType: dest,
        destinationDeviceId: dest === 'device' ? destDevice : undefined, destinationPath: dest === 'alternate' || dest === 'device' ? destPath.trim() : undefined,
        conflictPolicy: dest === 'browser' ? 'rename' : conflict, confirmOverwrite: dest !== 'browser' && confirmOverwrite, restoreMetadata: meta, restorePermissions: perms, reason: reason || undefined,
      });
      toast(tr('Getting your files back…'));
      nav(`${t.base}/restores/${r.id}`);
    } catch (e) {
      setErr(friendlyError(e));
      throw e;
    } finally {
      setBusy(false);
    }
  }).catch(() => undefined);
  if (!t.can('restores.create')) {
    return (
      <div>
        <PageHead title={tr('Get files back')} />
        <div className="notice warn"><strong>{tr('View-only access')}</strong>{tr('You can look through what is backed up, but only admins can get files back.')}</div>
      </div>
    );
  }
  const recent = mode === 'kept' ? estimate.data?.recentChange : null;
  const backedUp = all.filter((d) => d.last_backup_at || d.id === deviceId);
  return (
    <div>
      <PageHead
        title={tr('Get files back')}
        desc={tr('Bring back files or folders, even if the computer is off or gone. Each file comes back as its newest copy unless you choose an older one.')}
        actions={<Link className="btn ghost" to={`${t.base}/restores`}>{tr('Previous restores')}</Link>}
      />
      <div className="steps" aria-label={tr('Progress')}>
        {keys.map((k, i) => (
          <div key={k} className="row" style={{ gap: 0 }}>
            <div className={`st ${i === step ? 'on' : i < step ? 'done' : ''}`}><span className="n">{i < step ? '✓' : i + 1}</span>{titles[k]}</div>
            {i < keys.length - 1 && <span className="sep" />}
          </div>
        ))}
      </div>
      {stepKey === 'who' && (
        <Card title={tr('Whose files do you want back?')}>
          {devices.isLoading ? <Loading /> : backedUp.length === 0 ? (
            <Empty glyph="◷" title={tr('Nothing to get back yet')}>{tr('Files can be brought back after a computer finishes its first backup.')}</Empty>
          ) : (
            <div className="grid g3">
              {backedUp.map((d) => (
                <button key={d.id} className="card card-pad" style={{ textAlign: 'start', cursor: 'pointer', borderColor: deviceId === d.id ? 'var(--brass)' : undefined, background: deviceId === d.id ? 'var(--brass-soft)' : undefined }}
                  onClick={() => { setDeviceId(d.id); setDayRp(''); setPickingDay(false); setArchiveId(''); setSource('backup'); setDest('browser'); setSelected(new Set()); setChoices(new Map()); }}>
                  <strong>{d.display_name}</strong>
                  <div className="small muted">{d.last_backup_at ? tr('Last backup {time}', { time: ago(d.last_backup_at) }) : tr('Not backed up yet')}</div>
                  {d.status === 'retired' && <div className="small muted">{tr('Removed computer')}</div>}
                </button>
              ))}
            </div>
          )}
          <p className="small muted">{tr('The computer does not need to be on. You can also get files from a computer that was removed, lost or broken.')}</p>
        </Card>
      )}
      {stepKey === 'source' && (
        <Card title={tr('Where are the files?')}>
          <OptionCards
            name="source"
            value={source}
            onChange={chooseSource}
            options={[
              { key: 'backup', title: tr('In a saved backup'), desc: tr('Files as they were on the computer at a backup: also files that were deleted or changed since.') },
              {
                key: 'archive',
                title: tr('In an archive'),
                desc: tr('Old files that moved to the cloud to free up space on the computer.'),
                extra: (
                  <div className="stack" style={{ gap: 6 }}>
                    {archives.map((a) => (
                      <label key={a.id} className="check">
                        <input type="radio" name="archive" checked={archiveId === a.id} onChange={() => { setArchiveId(a.id); setSelected(new Set()); }} />
                        <span>
                          <Icons.folder className="ficon dir" width={16} /> <strong><bdi>{folderName(a.path, pathStyle)}</bdi></strong>
                          <span className="small muted"> · {tr('{files} files · {size}', { files: a.cloud.files.toLocaleString(locale()), size: bytes(a.cloud.bytes) })}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </Card>
      )}
      {stepKey === 'files' && (
        <div className="stack">
          {mode === 'archive' ? (archive && (
            <>
              <p className="muted" style={{ margin: 0 }}>{tr('Tick the files or folders you want. A ticked folder brings back everything inside it.')}</p>
              <ArchiveBrowser deviceId={deviceId} archive={archive} pathStyle={pathStyle} selectable selected={selected} onToggle={toggle} />
            </>
          )) : mode === 'day' ? (
            <>
              <div className="notice row between">
                <span><strong>{tr('Showing files as they were on {time}', { time: dayTime ? when(dayTime) : '…' })}</strong>{tr('Only what you tick comes back, exactly as it was then.')}</span>
                <Button size="sm" onClick={toNewest}>{tr('Back to newest')}</Button>
              </div>
              <RecoveryPointBrowser rpId={dayRp} deviceId={deviceId} pathStyle={pathStyle} selectable selected={selected} onToggle={toggle} {...(srcDevice?.kind === 'phone' ? { rootLabel: tr('My Files') } : {})} />
            </>
          ) : pickingDay ? (
            <DayPicker deviceId={deviceId} onPick={(b) => { setSelected(new Set()); setDayRp(b.id); setPickingDay(false); }} onCancel={() => setPickingDay(false)} />
          ) : (
            <>
              <div className="row between">
                <p className="muted" style={{ margin: 0 }}>{tr('Tick the files or folders you want. A ticked folder brings back everything inside it.')}</p>
                <button type="button" className="kept-older" onClick={toDay}>{tr('Bring a folder back as it was on a certain day')}</button>
              </div>
              <KeptFilesTree deviceId={deviceId} pathStyle={pathStyle} choices={choices} onChange={setChoices} {...(srcDevice?.kind === 'phone' ? { rootLabel: tr('My Files') } : {})} />
            </>
          )}
          {recent && (
            <div className="notice warn" role="status">
              <strong>{tr('Many of these files changed on {date}', { date: recent.changedAt ? shortDate(recent.changedAt) : '' })}</strong>
              {tr('If something damaged them, get them as they were before that.')}
              <div style={{ marginTop: 8 }}>
                <Button size="sm" onClick={() => { setSelected(new Set(choices.keys())); setChoices(new Map()); setDayRp(recent.before.id); }}>
                  {tr('Show files as they were on {date}', { date: recent.before.finishedAt ? shortDate(recent.before.finishedAt) : '' })}
                </Button>
              </div>
            </div>
          )}
          {!pickingDay && (
            <div className="notice selbar" aria-live="polite">
              <strong>{count === 1 ? tr('1 item chosen') : tr('{n} items chosen', { n: count })}</strong>
              {count > MAX_ITEMS ? tr('Too many separate items. Tick their folder instead.')
                : estimate.error ? friendlyError(estimate.error)
                : estimate.data ? (
                  <>
                    {tr('{files} files in total, {size}', { files: Number(estimate.data.files).toLocaleString(locale()), size: bytes(estimate.data.bytes) })}
                    {mode === 'kept' && older.length > 0 && <> · {older.length === 1 ? tr('1 older copy chosen') : tr('{n} older copies chosen', { n: older.length })}</>}
                  </>
                ) : count ? tr('Counting…') : tr('Tick at least one file or folder.')}
            </div>
          )}
        </div>
      )}
      {switchTo && (
        <ConfirmDialog
          title={switchTo === 'day' ? tr('Choose a day instead?') : tr('Back to the newest copies?')}
          confirm={switchTo === 'day' ? tr('Choose a day') : tr('Back to newest')}
          onConfirm={doSwitch}
          onClose={() => setSwitchTo(null)}
        >
          <p style={{ margin: 0 }}>{tr('What you ticked so far will be cleared, and you can tick again.')}</p>
        </ConfirmDialog>
      )}
      {stepKey === 'where' && (
        <Card title={tr('Where should the files go?')}>
          <OptionCards
            name="dest"
            value={dest}
            onChange={pick}
            options={[
              ...(fromArchive
                ? [{
                    key: 'original' as Dest,
                    title: tr('Put them back where they were'),
                    desc: tr('Back in the archive folder, as before. If a file with the same name is there, both are kept.'),
                    extra: (
                      <Field label={tr('Keep them on the computer for')} hint={tr('Old files would otherwise move to the cloud again at the next check.')}>
                        <Segmented value={hold} onChange={setHold} options={[
                          { key: '7', label: tr('{n} days', { n: 7 }) },
                          { key: '30', label: tr('{n} days', { n: 30 }) },
                          { key: '90', label: tr('{n} days', { n: 90 }) },
                          { key: 'forever', label: tr('Until I let them move') },
                        ]} />
                      </Field>
                    ),
                  }]
                : []),
              { key: 'browser', title: tr('Download them as a ZIP file'), desc: tr('Best for a few files. The download is ready in a few minutes (up to 10 GB).') },
              {
                key: 'alternate',
                title: tr('Save them in a new folder on {name}', { name: srcDevice?.display_name ?? '' }),
                desc: tr('Nothing on the computer is replaced. The computer must be on.'),
                extra: <Field label={tr('Folder')}><input type="text" className="mono" dir="ltr" value={destPath} onChange={(e) => setDestPath(e.target.value)} /></Field>,
              },
              ...(fromArchive ? [] : [{ key: 'original' as Dest, title: tr('Put them back where they were'), desc: tr('If a file with the same name is there, both are kept: the restored copy gets “(restored)” in its name.') }]),
              {
                key: 'device',
                title: tr('Send them to another computer'),
                desc: tr('For example, a new computer that replaces a broken one.'),
                extra: (
                  <div className="grid g2">
                    <Field label={tr('Computer')}>
                      <select value={destDevice} onChange={(e) => { setDestDevice(e.target.value); setDestPath(restoreFolder(others.find((o) => o.id === e.target.value)?.os)); }}>
                        <option value="">{tr('Choose…')}</option>
                        {others.map((d) => <option key={d.id} value={d.id}>{d.display_name}{d.protection?.online ? '' : ` (${tr('offline')})`}</option>)}
                      </select>
                    </Field>
                    <Field label={tr('Folder')}><input type="text" className="mono" dir="ltr" value={destPath} onChange={(e) => setDestPath(e.target.value)} /></Field>
                  </div>
                ),
              },
            ]}
          />
          {dest !== 'browser' && (
            <TechDetails summary={tr('More options')}>
              <Field label={tr('If a file with the same name is already there')}>
                <Segmented value={conflict} onChange={(v) => { setConflict(v); setConfirmOverwrite(false); }} options={[{ key: 'rename', label: tr('Keep both (recommended)') }, { key: 'skip', label: tr('Leave it, skip the restored one') }, { key: 'overwrite', label: tr('Replace it') }]} />
              </Field>
              {conflict === 'overwrite' && (
                <label className="check notice bad" style={{ marginBottom: 14 }}>
                  <input type="checkbox" checked={confirmOverwrite} onChange={(e) => setConfirmOverwrite(e.target.checked)} />
                  <span>{tr('I understand that files already there will be replaced by the restored versions.')}</span>
                </label>
              )}
              <label className="check" style={{ marginBottom: 8 }}><input type="checkbox" checked={meta} onChange={(e) => setMeta(e.target.checked)} /><span>{tr('Keep the original "last changed" dates')}</span></label>
              <label className="check" style={{ marginBottom: 8 }}><input type="checkbox" checked={perms} onChange={(e) => setPerms(e.target.checked)} /><span>{tr('Also bring back who may open the files (file permissions)')}</span></label>
            </TechDetails>
          )}
          {t.support && <Field label={tr('Reason (required for operators)')}><input type="text" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>}
        </Card>
      )}
      {stepKey === 'check' && (
        <Card title={tr('Check and start')}>
          <p style={{ marginTop: 0, fontSize: 15 }}>{summary()}</p>
          <TechDetails summary={tr('Chosen files and folders')}>
            {mode === 'kept'
              ? chosen.map((c) => (
                  <div key={c.path} className="small">
                    <span className="mono" dir="ltr">{vdisplay(c.path, pathStyle)}</span>
                    {c.olderFrom && <span className="muted"> · {tr('Copy from {date}', { date: shortDate(c.olderFrom) })}</span>}
                  </div>
                ))
              : [...selected].map((p) => <div key={p} className="mono small" dir="ltr">{vdisplay(p, pathStyle)}</div>)}
          </TechDetails>
          {dest !== 'browser' && conflict === 'overwrite' && <div className="notice bad" style={{ marginTop: 12 }}>{tr('Files already there will be replaced.')}</div>}
          <p className="small muted">{tr('Every file is checked to be exactly as it was backed up before it is saved.')}</p>
          {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
        </Card>
      )}
      <div className="row between" style={{ marginTop: 16 }}>
        <Button disabled={step === 0} onClick={() => setStepKey(keys[step - 1]!)}>{tr('Back')}</Button>
        {stepKey !== 'check'
          ? <Button variant="primary" disabled={!canNext[stepKey] || (stepKey === 'files' && pickingDay)} onClick={() => setStepKey(keys[step + 1]!)}>{tr('Continue')}</Button>
          : <Button variant="brass" busy={busy} disabled={conflict === 'overwrite' && dest !== 'browser' && !confirmOverwrite} onClick={submit}>{tr('Start')}</Button>}
      </div>
      {stepUp.dialog}
    </div>
  );
}

const RESTORE_STATE: Record<string, [string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral']> = {
  preparing: [msg('Preparing'), 'info'],
  queued: [msg('Waiting for the computer'), 'neutral'],
  running: [msg('In progress'), 'info'],
  completed: [msg('Done'), 'ok'],
  partial: [msg('Partly done'), 'warn'],
  failed: [msg("Didn't work"), 'bad'],
  canceled: [msg('Canceled'), 'neutral'],
  expired: [msg('Expired'), 'neutral'],
};
function RestoreState({ state }: { state: string }) {
  const [label, tone] = RESTORE_STATE[state] ?? [state, 'neutral'];
  return <Badge tone={tone}>{tr(label)}</Badge>;
}
/** A files-first restore in the history: every file its newest copy, or some older ones too. */
const copiesLabel = (copies: string) => (copies === 'mixed' ? tr('Newest copies and some older ones') : tr('Newest copies'));
const destText = (r: any) =>
  r.destination_type === 'browser' ? tr('ZIP download') : r.destination_type === 'original' ? tr('Back where they were') : <>{r.destination_device_name}: <span className="mono" dir="ltr">{r.destination_path}</span></>;

export function Restores() {
  const t = useTenant();
  const list = useQuery({ queryKey: ['restores', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/restores?limit=100`), refetchInterval: 10_000 });
  const nav = useNavigate();
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/restore`}>{arrow.back()} {tr('Get files back')}</Link>}
        title={tr('Previous restores')}
        actions={t.can('restores.create') ? <Link className="btn primary" to={`${t.base}/restore`}>{tr('Get files back')}</Link> : undefined}
      />
      <div className="card table-wrap">
        {list.isLoading ? <div className="card-pad"><Loading /></div> : list.error ? <div className="card-pad"><ErrorBox error={list.error} /></div> : list.data.restores.length === 0 ? (
          <Empty glyph="↺" title={tr('No restores yet')}>{tr('Trying to get a file back once is the best way to know your backups work.')}</Empty>
        ) : (
          <table>
            <thead><tr><th>{tr('When')}</th><th>{tr('From')}</th><th>{tr('To')}</th><th>{tr('Progress')}</th><th>{tr('Status')}</th><th>{tr('By')}</th></tr></thead>
            <tbody>
              {list.data.restores.map((r: any) => (
                <tr key={r.id} className="clickable" onClick={() => nav(`${t.base}/restores/${r.id}`)}>
                  <td className="small"><RowLink to={`${t.base}/restores/${r.id}`}>{when(r.created_at)}</RowLink></td>
                  <td>
                    {r.source_device_name}
                    {r.archive_path && <div className="small muted">{tr('Archive: {folder}', { folder: folderName(r.archive_path, r.archive_path.startsWith('/') && r.archive_path[2] === ':' ? 'windows' : 'posix') })}</div>}
                    {r.copies && <div className="small muted">{copiesLabel(r.copies)}</div>}
                  </td>
                  <td className="small">{destText(r)}</td>
                  <td style={{ width: 180 }}><Progress value={r.total_files ? (100 * Math.min(r.done_files, r.total_files)) / r.total_files : 0} /><div className="small muted num"><span className="ltr">{Math.min(r.done_files, r.total_files)} / {r.total_files}</span> {tr('files')}</div></td>
                  <td><RestoreState state={r.state} /></td>
                  <td className="small">{r.by_operator ? <Badge tone="bad">{tr('ZeroGap support')}</Badge> : <span className="mono">{r.requested_by_email}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function RestoreDetail() {
  const t = useTenant();
  const { restoreId } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [only, setOnly] = useState<'failed' | 'all'>('failed');
  const d = useQuery({ queryKey: ['restore', restoreId], queryFn: () => get(`/api/t/${t.tenantId}/restores/${restoreId}`), refetchInterval: (q) => (['completed', 'partial', 'failed', 'canceled', 'expired'].includes((q.state.data as any)?.restore?.state) ? false : 3000) });
  const items = useQuery({ queryKey: ['restore-items', restoreId, only], queryFn: () => get(`/api/t/${t.tenantId}/restores/${restoreId}/items${qs({ state: only === 'failed' ? 'failed' : undefined, limit: 200 })}`), refetchInterval: 5000 });
  const counts = useMemo(() => {
    const c = { done: 0, failed: 0, skipped: 0, pending: 0, total: 0, bytes: 0, doneBytes: 0 };
    for (const r of d.data?.counts ?? []) {
      if (r.kind === 'd') continue;
      c[r.state as 'done'] += r.n;
      c.total += r.n;
      c.bytes += Number(r.bytes);
      if (r.state === 'done') c.doneBytes += Number(r.bytes);
    }
    return c;
  }, [d.data]);
  if (d.isLoading) return <Loading />;
  if (d.error) return <ErrorBox error={d.error} />;
  const r = d.data.restore;
  const finished = ['completed', 'partial', 'failed', 'canceled', 'expired'].includes(r.state);
  const pct = counts.total ? (100 * (counts.done + counts.skipped + counts.failed)) / counts.total : 0;
  const headline =
    r.state === 'completed' ? (r.destination_type === 'browser' ? tr('Your download is ready') : tr('Done: all {n} files are back', { n: counts.done.toLocaleString(locale()) })) :
    r.state === 'partial' ? tr('Finished, but {n} files could not be brought back', { n: counts.failed }) :
    r.state === 'failed' ? tr("The restore didn't work") :
    r.state === 'canceled' ? tr('The restore was canceled') :
    r.state === 'expired' ? tr('The restore did not finish in time') :
    r.state === 'queued' ? tr('Waiting for the computer to start…') :
    tr('Getting your files back… {pct}%', { pct: Math.round(pct) });
  return (
    <div>
      <PageHead eyebrow={<Link to={`${t.base}/restores`}>{arrow.back()} {tr('Previous restores')}</Link>} title={tr('Files from {name}', { name: r.source_device_name })}
        desc={r.archive_path
          ? tr('From the archive {folder} · requested {requested}', { folder: folderName(r.archive_path, r.path_style), requested: ago(r.created_at) })
          : r.copies ? `${copiesLabel(r.copies)} · ${tr('requested {requested}', { requested: ago(r.created_at) })}`
          : tr('As they were on {time} · requested {requested}', { time: when(r.recovery_point_finished_at ?? r.created_at), requested: ago(r.created_at) })}
        actions={
          <>
            {!finished && t.can('restores.create') && <Button variant="danger" onClick={async () => { await post(`/api/t/${t.tenantId}/restores/${r.id}/cancel`); qc.invalidateQueries({ queryKey: ['restore', restoreId] }); }}>{tr('Cancel')}</Button>}
            {['partial', 'failed', 'canceled', 'expired'].includes(r.state) && r.destination_type !== 'browser' && t.can('restores.create') && (
              <Button onClick={async () => { try { await post(`/api/t/${t.tenantId}/restores/${r.id}/retry`); toast(tr('Trying the missing files again')); qc.invalidateQueries({ queryKey: ['restore', restoreId] }); } catch (e) { toast(friendlyError(e), true); } }}>{tr('Try the missing files again')}</Button>
            )}
          </>
        } />
      <div className="card card-pad">
        <div className="row between">
          <h2 style={{ fontSize: 22 }}>{headline}</h2>
          <RestoreState state={r.state} />
        </div>
        {!finished && <div style={{ marginTop: 12 }}><Progress value={pct} /></div>}
        <p className="muted" style={{ marginBottom: 0 }}>
          {tr('{done} of {total} files · {size}', { done: counts.done.toLocaleString(locale()), total: counts.total.toLocaleString(locale()), size: bytes(counts.doneBytes) })}
          {counts.skipped > 0 && <> · {tr('{n} skipped because a file with that name was already there', { n: counts.skipped })}</>}
        </p>
        {r.destination_type === 'browser' && (r.state === 'completed' || r.state === 'partial') && r.archive_object_key && (
          <div style={{ marginTop: 14 }}>
            <a className="btn brass" href={`/api/t/${t.tenantId}/restores/${r.id}/download`}><Icons.download width={16} />{tr('Download the ZIP file')}</a>
            {r.archive_expires_at && <span className="small muted" style={{ marginInlineStart: 10 }}>{tr('Available until {time}', { time: when(r.archive_expires_at) })}</span>}
          </div>
        )}
        {r.destination_type !== 'browser' && <p className="small" style={{ marginBottom: 0 }}>{tr('Where:')} {destText(r)}</p>}
        {r.error && <TechDetails summary={tr('Why?')}><span className="mono small" dir="ltr">{r.error}</span></TechDetails>}
      </div>
      <Card title={tr('Files')} actions={<Segmented value={only} onChange={setOnly} options={[{ key: 'failed', label: tr('Problems only') }, { key: 'all', label: tr('All') }]} />} pad={false}>
        {items.data?.items.length ? (
          <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
            <table>
              <tbody>{items.data.items.map((i: any) => (
                <tr key={i.idx}>
                  <td className="mono small" dir="ltr" style={{ textAlign: 'start' }}>{vdisplay(i.path, r.path_style)}</td>
                  <td><Badge tone={i.state === 'done' ? 'ok' : i.state === 'failed' ? 'bad' : 'neutral'}>{i.state === 'done' ? tr('Back') : i.state === 'failed' ? tr('Could not bring back') : i.state === 'skipped' ? tr('Skipped') : tr('Waiting')}</Badge></td>
                  <td className="small">{i.error ? <span className="mono" dir="ltr">{i.error}</span> : i.result_path ? <span className="mono" dir="ltr">{i.result_path}</span> : ''}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <Empty glyph={only === 'failed' ? '✓' : '·'} title={only === 'failed' ? tr('No problems') : tr('No files yet')} />}
      </Card>
    </div>
  );
}
